const SOL_MINT = "So11111111111111111111111111111111111111112";
const USDC_MINT = "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v";
const USDT_MINT = "Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB";
const QUOTE_MINTS = new Set([SOL_MINT, USDC_MINT, USDT_MINT]);

function solanaPairsFromTokensPayload(json) {
  const pairs = Array.isArray(json?.pairs) ? json.pairs : [];
  return pairs.filter((pair) => {
    if (!pair?.pairAddress) return false;
    if (pair.chainId && String(pair.chainId).toLowerCase() !== "solana") {
      return false;
    }
    return true;
  });
}

function solanaPairsFromTokenPairsPayload(json) {
  const raw = Array.isArray(json) ? json : [];
  return raw.filter((pair) => Boolean(pair?.pairAddress));
}

function pairFromPairsEndpoint(json) {
  if (json?.pair?.pairAddress) return json.pair;
  if (Array.isArray(json?.pairs) && json.pairs[0]?.pairAddress) {
    return json.pairs[0];
  }
  return null;
}

function pairLiquidityUsd(pair) {
  const n = Number(pair?.liquidity?.usd);
  return Number.isFinite(n) ? n : 0;
}

function bestPair(pairs) {
  if (!Array.isArray(pairs) || pairs.length === 0) return null;
  return [...pairs].sort((a, b) => pairLiquidityUsd(b) - pairLiquidityUsd(a))[0];
}

/**
 * @param {object} pair
 * @param {string} submittedAddress
 * @returns {{ mint: string, meta: object, resolvedFromPool: boolean } | null}
 */
export function pickMintMetaFromDexPair(pair, submittedAddress) {
  const submitted = String(submittedAddress || "").trim();
  const base = pair?.baseToken || {};
  const quote = pair?.quoteToken || {};

  if (submitted && submitted === base.address) {
    return { mint: base.address, meta: base, resolvedFromPool: false };
  }
  if (submitted && submitted === quote.address && !QUOTE_MINTS.has(quote.address)) {
    return { mint: quote.address, meta: quote, resolvedFromPool: false };
  }
  if (base.address && !QUOTE_MINTS.has(base.address)) {
    return {
      mint: base.address,
      meta: base,
      resolvedFromPool: submitted !== base.address,
    };
  }
  if (quote.address && !QUOTE_MINTS.has(quote.address)) {
    return {
      mint: quote.address,
      meta: quote,
      resolvedFromPool: submitted !== quote.address,
    };
  }
  return null;
}

/**
 * Map a DexScreener pair to the same shape Create Bot validation expects.
 * @param {object} pair
 * @param {string} submittedAddress
 */
export function tokenInfoFromDexPair(pair, submittedAddress) {
  const picked = pickMintMetaFromDexPair(pair, submittedAddress);
  if (!picked?.mint) return null;
  const priceUsd = Number(pair?.priceUsd);
  const liquidity = pairLiquidityUsd(pair);
  return {
    tokenMint: picked.mint,
    tokenName: picked.meta?.name || "Unknown Token",
    tokenSymbol: picked.meta?.symbol || "UNKNOWN",
    poolAddress: String(pair?.pairAddress || "").trim() || null,
    dexId: pair?.dexId ? String(pair.dexId).trim() : null,
    price:
      Number.isFinite(priceUsd) && priceUsd > 0 ? priceUsd.toFixed(6) : null,
    priceUsd: Number.isFinite(priceUsd) ? priceUsd : null,
    liquidity,
    resolvedFromPool: picked.resolvedFromPool,
  };
}

async function fetchJson(url, fetchImpl) {
  const res = await fetchImpl(url, {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) return null;
  return res.json().catch(() => null);
}

/**
 * Look up a Solana mint or DexScreener pair on Pump.fun / LaunchLab / AMM.
 * Jupiter often misses bonding-curve tokens; DexScreener indexes them.
 *
 * @param {string} address
 * @param {typeof fetch} [fetchImpl]
 * @returns {Promise<object | null>}
 */
export async function lookupSolanaTokenOnDexScreener(
  address,
  fetchImpl = fetch,
) {
  const trimmed = String(address || "").trim();
  if (!trimmed) return null;

  try {
    const [tokensJson, tokenPairsJson] = await Promise.all([
      fetchJson(
        `https://api.dexscreener.com/latest/dex/tokens/${encodeURIComponent(trimmed)}`,
        fetchImpl,
      ),
      fetchJson(
        `https://api.dexscreener.com/token-pairs/v1/solana/${encodeURIComponent(trimmed)}`,
        fetchImpl,
      ),
    ]);

    let pair = bestPair([
      ...solanaPairsFromTokensPayload(tokensJson),
      ...solanaPairsFromTokenPairsPayload(tokenPairsJson),
    ]);

    if (!pair) {
      const pairJson = await fetchJson(
        `https://api.dexscreener.com/latest/dex/pairs/solana/${encodeURIComponent(trimmed)}`,
        fetchImpl,
      );
      pair = pairFromPairsEndpoint(pairJson);
    }

    if (!pair) return null;
    return tokenInfoFromDexPair(pair, trimmed);
  } catch {
    return null;
  }
}
